import { Box } from "@rebass/grid";
import { IAccount } from "@rentiohq/shared-frontend/dist/types/auth.types";
import { IDocument } from "@rentiohq/shared-frontend/dist/types/document.types";
import { EDocumentPackageTypes } from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import {
  AccountList,
  Activities,
  Banner,
  Card,
  DisplayText,
  DocumentFetchListItem,
  ESpacings,
} from "@rentiohq/web-shared/dist/components";
import { useDispatch } from "react-redux";
import { ts } from "../../../../../services";
import * as documentPackageUtils from "../../../../../utils/documentPackage.utils";
import { ContractRows } from "../../../../ContractRows";
import { DocumentRow } from "../../RentDeposit/components/RentDepositInfo/components/DocumentRow";
import {
  EDocumentPackageTabs,
  IDocumentPackageTabProps,
} from "../DocumentPackage.types";
import { generateGetDocumentPackageSignerActions } from "./DocumentPackageInfo.utils";

export const DocumentPackageInfo = (props: IDocumentPackageTabProps) => {
  const { documentPackage, setTab } = props;

  const dispatch = useDispatch();

  const handleActivityMoreClick = () => {
    setTab(EDocumentPackageTabs.History);
  };

  const renderCardHeader = (heading: string) => (
    <DisplayText size="extraSmall" space="tight">
      {heading}
    </DisplayText>
  );

  const renderDocument = (document: IDocument) => {
    return (
      <DocumentRow
        hasRevoke={false}
        documentPackage={{ ...documentPackage, documentsInPackage: [document] }}
      />
    );
  };

  const createSignerSubtitle = (accountIds: number[]) => {
    const signer = documentPackageUtils.findUniqueSigner(
      documentPackage,
      accountIds,
    );
    if (!signer) {
      return null;
    }

    return documentPackageUtils.renderSignerSubtitle(signer);
  };

  const uniqueSigners = documentPackageUtils.getUniqueSigners(documentPackage);

  const renderRentalContractBanner =
    documentPackage.type === EDocumentPackageTypes.Contract &&
    documentPackageUtils.hasAllTenantSignatures(documentPackage) ? (
      <Box mb={ESpacings.loose}>
        <Banner
          variation="warning"
          // icon="alertDiamond"
          title={ts.documentPackageContractWaitingForTenantNoticeHeading()}
          hasDismiss={false}
        >
          {ts.documentPackageContractWaitingForTenantNoticeContent()}
        </Banner>
      </Box>
    ) : null;

  return (
    <>
      {documentPackage.documentsInPackageIds && (
        <Card
          heading={renderCardHeader(ts.system("documents"))}
          space={ESpacings.base}
        >
          <DocumentFetchListItem
            documentId={documentPackage.documentsInPackageIds[0]}
            renderDocument={renderDocument}
          />
        </Card>
      )}

      <Card
        heading={renderCardHeader(ts.system("signers"))}
        space={ESpacings.base}
      >
        {renderRentalContractBanner}

        <AccountList
          accounts={
            documentPackage
              ? (uniqueSigners.map(signer => ({
                  id: signer.accountId,
                  firstname: signer.firstname,
                  lastname: signer.lastname,
                  company: signer.company,
                })) as IAccount[])
              : []
          }
          getActions={generateGetDocumentPackageSignerActions({
            dispatch,
            documentPackage,
          })}
          getSubtitle={createSignerSubtitle}
        />
      </Card>

      {documentPackage.contractId && (
        <Card
          heading={renderCardHeader(ts.system("contract"))}
          space={ESpacings.base}
        >
          <ContractRows contractIds={[documentPackage.contractId]} />
        </Card>
      )}

      <Card
        heading={renderCardHeader(ts.system("history"))}
        space={ESpacings.base}
      >
        <Box mb={ESpacings.base}>
          <Activities
            identifier={`document-package-${documentPackage.id}-compact`}
            filter={documentPackageUtils.getDocumentPackageActivityFilter({
              documentPackage,
            })}
            limit={3}
            isCompact={true}
            onMoreClick={handleActivityMoreClick}
          />
        </Box>
      </Card>
    </>
  );
};
